import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EditExternalAtmosphericLogModal } from 'Components/RocketDry';

import { useRocketDryFunctions } from 'Context/RocketDry';

import { numberInputRegex } from 'Containers/RocketDry/helpers';
import { parseISODate } from 'Utils/helpers';

import { updateExternalAtmosphericLog } from 'Containers/RocketDry/actions';
import { createExternalAtmosphericLogErrorsSelector } from 'Containers/RocketDry/selectors';
import { areEqual } from 'Utils/equalityChecks';

function EditExternalAtmosphericLogModalContainer() {
  const dispatch = useDispatch();

  const {
    isEditExternalAtmosphericLogModalOpen,
    setIsEditExternalAtmosphericLogModalOpen,
    selectedAtmosphericLog,
    setSelectedAtmosphericLog,
  }: any = useRocketDryFunctions();

  const createExternalAtmosphericLogErrors = useSelector(createExternalAtmosphericLogErrorsSelector, areEqual);

  const [relativeHumidity, setRelativeHumidity] = useState('');
  const [temperature, setTemperature] = useState('');
  const [pressure, setPressure] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [recordTime, setRecordTime] = useState(undefined);

  useEffect(() => {
    if (selectedAtmosphericLog) {
      setRelativeHumidity(selectedAtmosphericLog?.relativeHumidity ?? '');
      setTemperature(selectedAtmosphericLog?.temperature ?? '');
      setPressure(selectedAtmosphericLog?.pressure ?? '');
      setWindSpeed(selectedAtmosphericLog?.windSpeed ?? '');
      const formattedRecordTime = selectedAtmosphericLog?.createdAt
        ? parseISODate(selectedAtmosphericLog.createdAt)
        : undefined;
      setRecordTime(formattedRecordTime);
    }
  }, [selectedAtmosphericLog]);

  const onChangeRelativeHumidity = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setRelativeHumidity(filtered);
  }, []);

  const onChangeTemperature = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setTemperature(filtered);
  }, []);

  const onChangePressure = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setPressure(filtered);
  }, []);

  const onChangeWindSpeed = useCallback(({ target: { value } }) => {
    const filtered = value.replace(numberInputRegex, '');
    setWindSpeed(filtered);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsEditExternalAtmosphericLogModalOpen(false);
    setSelectedAtmosphericLog({});
  }, []);

  const onSaveExternalButtonClick = useCallback(() => {
    if (selectedAtmosphericLog?.id) {
      const createdAt = new Date(recordTime).toISOString();
      dispatch(
        updateExternalAtmosphericLog(
          selectedAtmosphericLog.id,
          relativeHumidity,
          temperature,
          pressure,
          windSpeed,
          createdAt,
          onCloseModal
        )
      );
    }
  }, [selectedAtmosphericLog, relativeHumidity, temperature, pressure, windSpeed, recordTime]);

  return (
    <EditExternalAtmosphericLogModal
      isOpen={isEditExternalAtmosphericLogModalOpen}
      closeModal={onCloseModal}
      relativeHumidity={relativeHumidity}
      temperature={temperature}
      pressure={pressure}
      windSpeed={windSpeed}
      recordTime={recordTime}
      formErrors={createExternalAtmosphericLogErrors}
      onSaveExternalButtonClick={onSaveExternalButtonClick}
      onChangeRelativeHumidity={onChangeRelativeHumidity}
      onChangePressure={onChangePressure}
      onChangeTemperature={onChangeTemperature}
      onChangeWindSpeed={onChangeWindSpeed}
      onChangeRecordTime={setRecordTime}
    />
  );
}

const EditExternalAtmosphericLogModalMemo = memo(EditExternalAtmosphericLogModalContainer);

export { EditExternalAtmosphericLogModalMemo as EditExternalAtmosphericLogModal };
